import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/lib/images";
import { Button } from "@/components/ui/button";
import { CheckCircle2, PackageSearch } from "lucide-react";
import { ShippingPayModal } from "@/components/ShippingPayModal";
import { FindoSpinner } from "@/components/FindoSpinner";

// The former full-page shipping form has been replaced by a confirmation modal
// (opened from « Payer les frais d'expédition »). This route stays for deep
// links (emails / bell) and simply auto-opens that modal.
export default function Ship() {
  const { declarationId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [decl, setDecl] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    api(`/declarations/${declarationId}/public`, { auth: false })
      .then((d) => {
        setDecl(d);
        if (!d.is_shipping_paid && d.item_preview?.shipping_available) setModalOpen(true);
      })
      .catch(() => setNotFound(true));
  }, [declarationId]);

  if (notFound) return <div className="max-w-md mx-auto py-32 text-center text-slate-500">{t("ship.notfound")}</div>;
  if (!decl) return <div className="max-w-md mx-auto py-32 text-center text-slate-400"><FindoSpinner size={24} className="mx-auto" /></div>;

  return (
    <div className="bg-background">
      <div className="relative h-56 overflow-hidden photo-scrim">
        <img src={IMAGES.hallway} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6">
          <span className="inline-flex items-center gap-2 rounded-full badge-red px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">{t("hc.payment")}</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold">{t("hc.payTheShippingFees")}</h1>
          <p className="mt-2 text-white/85 max-w-lg">{t("hc.payForTheShipmentOf")}</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-6 py-12">
        {decl.is_shipping_paid ? (
          <div className="bg-white rounded-2xl border border-border card-shadow p-8 text-center" data-testid="ship-already-paid">
            <div className="mx-auto h-16 w-16 grid place-items-center rounded-2xl bg-emerald-100 text-emerald-700"><CheckCircle2 size={34} /></div>
            <h2 className="mt-5 font-display text-2xl font-semibold text-findo-navy">{t("hc.shippingPaid")}</h2>
            <p className="mt-2 text-slate-600">{t("hc.yourShippingFeesHaveBeen")}</p>
            <div className="mt-6"><Link to={`/track/${declarationId}`}><Button variant="outline" className="rounded-full font-semibold">{t("trk.title")}</Button></Link></div>
          </div>
        ) : !decl.item_preview?.shipping_available ? (
          <div className="bg-white rounded-2xl border border-border card-shadow p-8 text-center" data-testid="ship-not-shippable">
            <PackageSearch size={32} className="mx-auto text-slate-300" />
            <p className="mt-3 text-slate-500">{t("ship.notShippable")}</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-border card-shadow p-8 text-center" data-testid="ship-card">
            <p className="text-slate-600">{t("hc.confirmYourShippingAddressTo")}</p>
            <Button onClick={() => setModalOpen(true)} className="mt-5 rounded-full font-semibold bg-findo-navy hover:bg-findo-navy/90" data-testid="ship-open-modal-btn">
              {t("hc.payTheShippingFees")}
            </Button>
          </div>
        )}
      </div>

      <ShippingPayModal declarationId={declarationId} open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
